import React, { useCallback, useState, useRef, useEffect } from 'react';
import classname from '@search/classname/src';
import { Input, Button, Typography } from '@vtblife/uikit';
import { useCustomerCreate } from '../../hooks/useCustomerCreate';
import { ICustomer } from '../../types/ICustomer';

import './AgentCustomersRadioCreate.css';

const cn = classname.bind(null, 'AgentCustomersRadioCreate');

export interface IOfferCreateItem {
    onAddCustomer: (args: ICustomer) => void;
}

export const AgentCustomersRadioCreate: React.FC<IOfferCreateItem> = ({ onAddCustomer }) => {
    const [ isLoading, setLoading ] = useState(false);
    const { onCreateCustomer } = useCustomerCreate();

    const [ isEditing, setEditing ] = useState(false);

    const handleClickByCreate = useCallback(() => {
        setEditing(true);
    }, []);

    const handleSubmitEditing = useCallback((data: { name: string }) => {
        setLoading(true);
        return onCreateCustomer({
            note: '',
            email: '',
            phone: '',
            name: data.name
        }).then(res => {
            if (res) {
                onAddCustomer(res);
                setEditing(false);
                return res;
            }

            return undefined;
        }).then(() => {
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [ onCreateCustomer, onAddCustomer ]);

    return isEditing ? (
        <OfferCreateItemInput
            onSubmitEditing={handleSubmitEditing}
            isLoading={isLoading}
            onClose={() => {
                setEditing(false);
            }}
        />
    ) : (
        <OfferCreateItemButton onClickByCreate={handleClickByCreate} />
    );
};

interface IOfferCreateItemInput {
    onSubmitEditing: (data: { name: string }) => Promise<void>;
    onClose: () => void;
    isLoading: boolean;
}

export const OfferCreateItemInput: React.FC<IOfferCreateItemInput> = ({ onSubmitEditing, isLoading, onClose }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [ isError, setError ] = useState(true);
    const [ name, setName ] = useState('');

    const handleChangeName = useCallback((value: string) => {
        setName(value);
        setError(value.trim().length < 1);
    }, []);

    const handleResetInput = useCallback(() => {
        setName('');
        setError(true);
        inputRef.current?.focus();
    }, [ inputRef ]);

    const handleSubmit = useCallback((event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (! isError) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            onSubmitEditing({
                name
            }).then(handleResetInput);
        }
    }, [ isError, name, onSubmitEditing, handleResetInput ]);

    useEffect(() => {
        inputRef.current?.focus();
    }, [ inputRef ]);

    return (
        <form name='OfferCreateItemForm' onSubmit={handleSubmit} className={cn('form')}>
            <div className={cn('title')}>
                <Typography variant='h4'>Новый клиент</Typography>
            </div>
            <div className={cn('input')}>
                <Input
                    inputRef={inputRef}
                    size='m'
                    disabled={isLoading}
                    value={name}
                    error={isError}
                    onChange={handleChangeName}
                    placeholder='Имя клиента'
                    dataTest='new-customer-name-input'
                    loading={isLoading}
                    onReset={false}
                />
            </div>
            <div className={cn('btns')}>
                <Button block size='s' variant='secondary' onClick={onClose}>Отмена</Button>
                <Button block size='s' type='submit' variant='secondary'>Добавить</Button>
            </div>
        </form>
    );
};

interface IOfferCreateItemButton {
    onClickByCreate: VoidFunction;
}

const OfferCreateItemButton: React.FC<IOfferCreateItemButton> = ({ onClickByCreate }) => {
    const handleClickByCreate = useCallback(
        (event: React.MouseEvent<Element>) => {
            event.preventDefault();
            event.stopPropagation();
            onClickByCreate();
        },
        [ onClickByCreate ]
    );

    return (
        <Button
            block
            variant='secondary'
            icon='plus-circle'
            onClick={handleClickByCreate}
            size='m'
            data-gtm-zbs={'zbs-submit-save-customer'}
            dataTest='create-new-customer'
        >
            Добавить клиента
        </Button>
    );
};
